import { Text } from '@change/ds-core-ui';
import { ComboBox, ComboBoxItem } from '@change/ds-forms';
import { FlagIcon, LocationPinIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';

import type { LocationItemTypeEnum } from '#src/app/pages/search/shared/types';

import { useLocationFilter } from './hooks/useLocationFilter';
import { useTranslations } from './hooks/useTranslations';

type LocationFilterProps = {
	showTitle?: boolean;
};

export const LocationFilter: React.FC<LocationFilterProps> = ({ showTitle }) => {
	const {
		data: {
			data: { isLoading, isError, locations, selectedKey },
			searchTerm,
		},
		actions: { handleInputFocus, handleInputChange, handleInputBlur, selectLocation },
	} = useLocationFilter();

	const {
		data: { placeholder, label },
	} = useTranslations();

	return (
		<div className="w-full">
			{showTitle && (
				<Text
					as="h2"
					variant="heading"
					size="x-small"
					fontWeight="normal"
					className="mb-4"
					data-testid="location-filter-title"
				>
					<Translate value="corgi.search.filters.location.title" />
				</Text>
			)}
			<ComboBox
				name="location-filter"
				className="w-full"
				items={locations}
				defaultItems={locations}
				label={label}
				labelIsVisible={false}
				placeholder={placeholder}
				renderEmptyState={() => {
					return (
						<>
							{isLoading && (
								<div className="flex flex-col gap-2 p-2" data-testid="location-results-loading">
									<ListItemLoader />
									<ListItemLoader />
									<ListItemLoader />
									<ListItemLoader />
									<ListItemLoader />
									<ListItemLoader />
									<ListItemLoader />
									<ListItemLoader />
								</div>
							)}
							{isError && (
								<div className="flex flex-col items-center px-1 py-10">
									<Text as="p" className="text-typography-weak text-center">
										<Translate value="corgi.search.filters.location.error" />
									</Text>
								</div>
							)}
							{!isLoading && !isError && (
								<div className="flex flex-col items-center px-1 py-10">
									<Text as="p" className="text-typography-weak">
										<Translate value="corgi.search.filters.location.no_results" />
									</Text>
								</div>
							)}
						</>
					);
				}}
				showSearchIcon
				showCaretDownIcon={false}
				allowsEmptyCollection
				onFocus={handleInputFocus}
				onInputChange={handleInputChange}
				onBlur={handleInputBlur}
				onSelectionChange={(searchValue) => selectLocation(searchValue as string)}
				data-qa="location-filter-combobox"
				inputValue={searchTerm}
				selectedKey={selectedKey}
			>
				{(item) => <ComboBoxItem id={item.value} label={item.label} leadingIcon={<LocationIcon type={item.type} />} />}
			</ComboBox>
		</div>
	);
};

const LocationIcon: React.FC<{ type: LocationItemTypeEnum }> = ({ type }) => {
	if (type === 'country') {
		return <FlagIcon />;
	}
	return <LocationPinIcon />;
};

const ListItemLoader: React.FC = () => {
	return (
		<div className="animate-pulse">
			<div className="rounded-small bg-fill-press h-8 w-full" />
		</div>
	);
};
